import { Preferences } from '@capacitor/preferences';
import type { StoredUser } from '../types/common';

export type ThemePreference = 'light' | 'dark' | 'system';

const storageKeyToken = 'readifyUserKey';
const storageKeyThemePreference = 'readifyThemePreference';
// Legacy boolean key kept only for one-time migration to the new 3-way setting.
const storageKeyDarkModeLegacy = 'readifyDarkMode';
const storageKeyGlassSettings = 'readifyGlassSettings';

// Frosted-glass rendering of post cards. blur is in px; opacity is the glass
// tint alpha as a 0-100 percentage.
export interface GlassSettings {
  enabled: boolean;
  blur: number;
  opacity: number;
}

// Defaults mirror the .glass recipe (blur(24px), bg1 tint ≈ 0.55 alpha).
export const defaultGlassSettings: GlassSettings = {
  enabled: true,
  blur: 24,
  opacity: 55,
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const isThemePreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

const saveUser = (user: StoredUser): void =>
  localStorage.setItem(storageKeyToken, JSON.stringify(user));

const loadUser = (): StoredUser | null => {
  const item = localStorage.getItem(storageKeyToken);
  return item ? JSON.parse(item) : null;
};

const logoutUser = (): void => localStorage.removeItem(storageKeyToken);

const saveGlassSettings = (settings: GlassSettings): void =>
  localStorage.setItem(storageKeyGlassSettings, JSON.stringify(settings));

// Merge over defaults and clamp, so a stale/partial stored value (or future
// shape changes) can never produce an unusable glass config.
const loadGlassSettingsLocal = (): GlassSettings => {
  try {
    const stored = JSON.parse(localStorage.getItem(storageKeyGlassSettings) || '{}');
    return {
      enabled:
        typeof stored.enabled === 'boolean' ? stored.enabled : defaultGlassSettings.enabled,
      blur:
        typeof stored.blur === 'number'
          ? clamp(stored.blur, 0, 40)
          : defaultGlassSettings.blur,
      opacity:
        typeof stored.opacity === 'number'
          ? clamp(stored.opacity, 10, 95)
          : defaultGlassSettings.opacity,
    };
  } catch {
    return defaultGlassSettings;
  }
};

const saveThemePreference = (value: ThemePreference): Promise<void> => {
  localStorage.setItem(storageKeyThemePreference, value);
  return Preferences.set({ key: storageKeyThemePreference, value }).catch(() => {});
};

// Synchronous read for first paint. Falls back to migrating the legacy dark-mode
// boolean, defaulting to 'system'.
const loadThemePreferenceLocal = (): ThemePreference => {
  const stored = localStorage.getItem(storageKeyThemePreference);
  if (isThemePreference(stored)) {
    return stored;
  }

  const legacy = localStorage.getItem(storageKeyDarkModeLegacy);
  if (legacy === 'true') return 'dark';
  if (legacy === 'false') return 'light';

  return 'system';
};

// Async read that also consults Capacitor Preferences (native), keeping
// localStorage in sync so subsequent synchronous reads are correct.
const loadThemePreference = async (): Promise<ThemePreference> => {
  try {
    const { value } = await Preferences.get({ key: storageKeyThemePreference });
    if (isThemePreference(value)) {
      localStorage.setItem(storageKeyThemePreference, value);
      return value;
    }
  } catch (err) {
    return loadThemePreferenceLocal();
  }

  return loadThemePreferenceLocal();
};

const storage = {
  saveUser,
  loadUser,
  logoutUser,
  saveThemePreference,
  loadThemePreferenceLocal,
  loadThemePreference,
  saveGlassSettings,
  loadGlassSettingsLocal,
};

export default storage;
